import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2 } from "lucide-react";
import { fileToCsv, FILE_ACCEPT } from "@/lib/fileToCsv";

/**
 * Bulk-import leads into a CRM board from a CSV / XLSX.
 *
 * Props:
 *   open, onOpenChange   — controlled Dialog state
 *   boards               — list of {id, name, columns:[{key,label}...]}
 *   defaultBoardId       — pre-select this board (defaults to first)
 *   onImported           — (result) => void — called after a successful import
 *   simple               — when true, strip the dialog down to just "List
 *                          name" + "Upload from local drive". Used from the
 *                          Call Lists page where reps don't care about
 *                          board/column/preset/enrich/skip-trace — they
 *                          just want to name a CSV and drop it in.
 */
export default function ImportLeadsDialog({
  open, onOpenChange, boards = [], defaultBoardId = null, onImported,
  simple = false,
}) {
  const [csv, setCsv] = useState("");
  const [boardId, setBoardId] = useState(defaultBoardId || boards[0]?.id || "");
  const [columnKey, setColumnKey] = useState("");
  const [autoEnrich, setAutoEnrich]     = useState(false);
  const [autoSkipTrace, setAutoSkipTrace] = useState(false);
  const [listName, setListName]         = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null); // { done, total, batch }
  // Board-template presets tuned to a list's purpose (Foreclosure / Absentee /
  // Probate / …). When the rep picks one, we spin up a fresh board with the
  // preset's columns before importing so the leads land in a proper workflow.
  const [listPresets, setListPresets] = useState([]);
  const [presetKey, setPresetKey] = useState("");    // "" = use the existing board picked above
  useEffect(() => {
    if (!open) return;
    api.get("/boards/list-presets")
      .then(({ data }) => setListPresets(data.presets || []))
      .catch(() => {});
  }, [open]);

  const activeBoard = boards.find((b) => b.id === boardId) || boards[0];
  const columns = activeBoard?.columns || [];

  // Effective column key = user-picked OR first column of the board
  const effectiveCol = columnKey || columns[0]?.key || "";

  // Row count (excluding header) — used to warn about big imports + auto-toggle enrichment
  const rowCount = csv.trim()
    ? Math.max(0, csv.trim().split(/\r?\n/).length - 1)
    : 0;
  const LARGE_IMPORT_THRESHOLD = 1000;
  const CHUNK_SIZE = 500;
  const isLarge = rowCount >= LARGE_IMPORT_THRESHOLD;

  // When the CSV crosses the "big" threshold, auto-disable enrichment /
  // skip-trace so the user doesn't accidentally burn hundreds of REAPI credits.
  // They can flip them back on manually if they really want to.
  useEffect(() => {
    if (isLarge) {
      setAutoEnrich(false);
      setAutoSkipTrace(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLarge]);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const text = await fileToCsv(f);
      setCsv(text);
      toast.success(`Loaded ${f.name}`);
    } catch (err) {
      toast.error(err?.message || "Couldn't read the file");
    }
    e.target.value = "";
  };

  // Split a CSV string into N-row chunks, preserving the header on each chunk.
  const chunkCsv = (csvText, chunkSize) => {
    const lines = csvText.replace(/\r\n/g, "\n").split("\n").filter((l) => l.length > 0);
    if (lines.length <= 1) return [csvText];
    const header = lines[0];
    const rows = lines.slice(1);
    const chunks = [];
    for (let i = 0; i < rows.length; i += chunkSize) {
      chunks.push([header, ...rows.slice(i, i + chunkSize)].join("\n"));
    }
    return chunks;
  };

  const runImport = async () => {
    if (!csv.trim()) return toast.error(simple ? "Please upload a file first" : "Paste or upload a CSV first");
    if (simple && !listName.trim()) return toast.error("Please give this list a name");
    setBusy(true);
    setResult(null);
    setProgress(null);

    // Step 0 — if the rep picked a preset, spin up a fresh board with the
    // preset's columns FIRST, then land the leads inside it.
    let targetBoardId = boardId;
    let targetColumnKey = effectiveCol;
    if (presetKey) {
      try {
        const boardLabel = (listName.trim() || presetKey.replace(/_/g, " ")).slice(0, 60);
        const { data: newBoard } = await api.post("/boards/from-list-preset", {
          preset_key: presetKey,
          board_name: boardLabel,
        });
        targetBoardId = newBoard.id;
        targetColumnKey = (newBoard.columns || [])[0]?.key || "";
        toast.success(`Created board "${newBoard.name}" from ${presetKey} preset`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Preset board creation failed — using default board instead");
      }
    }

    // Small imports → single POST (current behavior)
    if (!isLarge) {
      try {
        const { data } = await api.post("/leads/import-csv", {
          csv_text: csv,
          board_id: targetBoardId || null,
          column_key: targetColumnKey || null,
          auto_enrich: autoEnrich,
          auto_skip_trace: autoSkipTrace,
          list_name: listName.trim() || null,
        });
        setResult(data);
        toast.success(`${data.created} created · ${data.updated} updated · ${data.skipped} skipped`);
        onImported?.(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Import failed");
      } finally { setBusy(false); }
      return;
    }

    // Large imports → chunked upload with progress bar
    const chunks = chunkCsv(csv, CHUNK_SIZE);
    setProgress({ done: 0, total: chunks.length, batch: CHUNK_SIZE });
    const agg = { created: 0, updated: 0, skipped: 0, enriched: 0,
                  skip_traced: 0, enrich_cache_hits: 0, errors: [] };
    for (let i = 0; i < chunks.length; i += 1) {
      try {
        const { data } = await api.post("/leads/import-csv", {
          csv_text: chunks[i],
          board_id: targetBoardId || null,
          column_key: targetColumnKey || null,
          auto_enrich: autoEnrich,
          auto_skip_trace: autoSkipTrace,
          list_name: listName.trim() || null,
        });
        agg.created += data.created || 0;
        agg.updated += data.updated || 0;
        agg.skipped += data.skipped || 0;
        agg.enriched += data.enriched || 0;
        agg.skip_traced += data.skip_traced || 0;
        agg.enrich_cache_hits += data.enrich_cache_hits || 0;
        if (Array.isArray(data.errors)) agg.errors.push(...data.errors);
        // Keep board_name from last successful chunk
        agg.board_name = data.board_name;
      } catch (e) {
        agg.errors.push(`Chunk ${i + 1}/${chunks.length}: ${e?.response?.data?.detail || e.message}`);
      }
      setProgress({ done: i + 1, total: chunks.length, batch: CHUNK_SIZE });
    }
    setResult(agg);
    toast.success(`Imported ${agg.created} new + ${agg.updated} updated across ${chunks.length} batches`);
    onImported?.(agg);
    setBusy(false);
  };

  const reset = () => {
    setCsv("");
    setResult(null);
    setProgress(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => {
      if (!v) reset();
      onOpenChange(v);
    }}>
      <DialogContent
        className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="import-leads-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <FileSpreadsheet size={18} className="text-emerald-600" />
            {simple ? "Create New List" : "Bulk Import Leads"}
          </DialogTitle>
          <DialogDescription>
            {simple
              ? <>Give this list a name, then upload a CSV or Excel file from your computer. Every row becomes a lead in this list — you can dial or manage it from Call Lists.</>
              : <>Upload a CSV or Excel file with columns for <b>address</b>, <b>owner
                name</b>, <b>phone</b>, and <b>email</b>. Each row becomes a lead
                on the chosen board — duplicates (same address) merge in phone/email
                instead of creating a copy.</>}
          </DialogDescription>
        </DialogHeader>

        {/* Board / column picker — hidden in `simple` mode (Call Lists page).
            We still auto-pick the first board internally so the import
            payload is valid; the rep just doesn't have to know or care. */}
        {!simple && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Land on board</Label>
            <select
              value={boardId}
              onChange={(e) => { setBoardId(e.target.value); setColumnKey(""); }}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--app-line)] bg-white text-sm"
              data-testid="import-board-select"
            >
              {boards.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Column</Label>
            <select
              value={columnKey || columns[0]?.key || ""}
              onChange={(e) => setColumnKey(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--app-line)] bg-white text-sm"
              data-testid="import-column-select"
            >
              {columns.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>
        )}

        {/* List name — user-defined bucket for this import */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest">
            {simple
              ? <>List name <span className="text-red-500 normal-case tracking-normal">*</span></>
              : <>Name this list <span className="text-muted-foreground normal-case tracking-normal">— optional but recommended</span></>}
          </Label>
          <input
            type="text"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            placeholder='e.g. "Cape Coral Absentee Nov 2026" or "Probate LA County"'
            className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--app-line)] bg-white text-sm"
            data-testid="import-list-name"
            maxLength={80}
          />
          <div className="text-[10px] text-muted-foreground mt-1">
            Every lead in this import is tagged with this name — creates a call-list bucket you can dial or manage separately from Lead Lists.
          </div>
        </div>

        {/* List purpose preset — spin up a dedicated board for this list */}
        {!simple && listPresets.length > 0 && (
          <div>
            <Label className="text-[10px] uppercase tracking-widest">
              Board template <span className="text-muted-foreground normal-case tracking-normal">— optional, creates a fresh workflow for this list</span>
            </Label>
            <select
              value={presetKey}
              onChange={(e) => setPresetKey(e.target.value)}
              data-testid="import-preset-select"
              className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--app-line)] bg-white text-sm"
            >
              <option value="">— Use existing board picked above —</option>
              {listPresets.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label} · {p.columns.length} cols
                </option>
              ))}
            </select>
            {presetKey && (() => {
              const p = listPresets.find((x) => x.key === presetKey);
              if (!p) return null;
              return (
                <div className="mt-2 rounded-lg border border-[var(--app-line)] bg-[var(--app-surface)]/50 p-3"
                     data-testid="import-preset-preview">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    New board columns
                  </div>
                  <div className="mt-1 text-[11px] leading-relaxed">
                    {p.description}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {p.columns.map((c) => (
                      <span key={c.key}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-[var(--app-line)]">
                        {c.label}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* File upload */}
        <label
          className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[var(--app-line)] bg-[var(--app-surface)]/40 px-4 py-6 text-sm cursor-pointer hover:bg-[var(--app-surface)] transition-colors"
          data-testid="import-file-drop"
        >
          <Upload size={14} className="text-muted-foreground" />
          <span className="text-muted-foreground">
            {simple
              ? "Click to upload from your computer — CSV or Excel"
              : "Click to upload CSV or Excel — or paste CSV below"}
          </span>
          <input
            type="file"
            accept={FILE_ACCEPT}
            onChange={handleFile}
            className="hidden"
            data-testid="import-file-input"
          />
        </label>

        {/* Paste area — hidden in `simple` mode (Call Lists page). Reps
            almost always drop a CSV file, not paste raw rows. Keeping the
            dialog focused on Name + Upload as requested. */}
        {!simple && (
        <div>
          <Label className="text-[10px] uppercase tracking-widest">
            Paste CSV (or preview the uploaded file)
          </Label>
          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder='address,owner_name,phone,email&#10;"123 Main St, Miami FL","John Doe","(305) 555-1234","john@example.com"'
            className="mt-1 font-mono text-xs h-32"
            data-testid="import-csv-textarea"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Recognised header names (case &amp; spacing don&apos;t matter): <b>address</b>,{" "}
            <b>property_address</b>, <b>owner</b>/<b>owner_name</b>,{" "}
            <b>phone</b>/<b>phone1</b>..N, <b>email</b>/<b>email1</b>..N.
          </p>
        </div>
        )}

        {/* Large-import banner + progress */}
        {isLarge && !result && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900"
               data-testid="import-large-banner">
            <div className="font-semibold flex items-center gap-1.5">
              <FileSpreadsheet size={13} /> Large import detected
              <span className="ml-1 font-mono">{rowCount.toLocaleString()} rows</span>
            </div>
            <div className="mt-1 text-[11px]">
              We&apos;ll upload this in <b>{Math.ceil(rowCount / CHUNK_SIZE)} batches of {CHUNK_SIZE}</b> so it doesn&apos;t time out.
              Auto-enrich and Auto-skip-trace have been switched <b>OFF</b> to protect your REAPI credits —
              you can enrich in batches of 100 from the CRM after import.
            </div>
          </div>
        )}
        {progress && (
          <div className="rounded-xl border border-sky-300 bg-sky-50 px-3 py-2.5 text-xs text-sky-900"
               data-testid="import-progress">
            <div className="flex items-center justify-between font-medium">
              <span>Uploading batch {progress.done}/{progress.total}</span>
              <span className="font-mono">
                {Math.min(progress.done * progress.batch, rowCount).toLocaleString()} / {rowCount.toLocaleString()}
              </span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-sky-200/60 overflow-hidden">
              <div
                className="h-full bg-sky-600 transition-all"
                style={{ width: `${Math.min(100, (progress.done / progress.total) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Auto-actions — hidden in `simple` mode. Reps just want to name
            a list and drop a CSV; enrich/skip-trace can be done later. */}
        {!simple && (
        <div className="rounded-xl border border-[var(--app-line)] bg-slate-50 p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            After import (skips leads that don&apos;t need it — no duplicate charges)
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer"
                 data-testid="import-auto-enrich-toggle">
            <input
              type="checkbox"
              checked={autoEnrich}
              onChange={(e) => setAutoEnrich(e.target.checked)}
              className="mt-1 accent-emerald-600"
            />
            <div>
              <div className="font-medium">Auto-enrich new leads <span className="text-[10px] font-normal text-muted-foreground">(off by default)</span></div>
              <div className="text-[11px] text-muted-foreground">
                Fetch ARV, equity %, motivation flags via REAPI (falls back to
                RentCast). <b>Skips any row that already has a value in the
                list</b>, so already-enriched lists cost nothing. ~1 REAPI
                credit per new lead that needs it. Cache hits are free.
              </div>
            </div>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer"
                 data-testid="import-auto-skiptrace-toggle">
            <input
              type="checkbox"
              checked={autoSkipTrace}
              onChange={(e) => setAutoSkipTrace(e.target.checked)}
              className="mt-1 accent-emerald-600"
            />
            <div>
              <div className="font-medium">Auto skip-trace missing phones</div>
              <div className="text-[11px] text-muted-foreground">
                Only runs on leads with <b>no phone AND no email</b>. Cache
                hits are free. Live REAPI SkipTrace calls cost roughly $0.10
                each — heads-up on large imports.
              </div>
            </div>
          </label>
        </div>
        )}

        {/* Result summary */}
        {result && (
          <div
            className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 text-sm p-3"
            data-testid="import-result"
          >
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 size={14} /> Import complete
            </div>
            <div className="mt-1 text-xs">
              <b>{result.created}</b> new · <b>{result.updated}</b> updated · <b>{result.skipped}</b> skipped
              {result.enriched > 0 && (
                <span className="ml-1">
                  · <b>{result.enriched}</b> enriched{result.enrich_cache_hits > 0 && ` (+${result.enrich_cache_hits} cache)`}
                </span>
              )}
              {result.skip_traced > 0 && (
                <span className="ml-1">· <b>{result.skip_traced}</b> skip-traced</span>
              )}
              {result.board_name && (
                <span className="ml-1 text-emerald-700">
                  · on <i>{result.board_name}</i>
                </span>
              )}
            </div>
            {result.errors?.length > 0 && (
              <details className="mt-2 text-[11px]">
                <summary className="cursor-pointer text-amber-800">
                  {result.errors.length} warning(s)
                </summary>
                <ul className="mt-1 space-y-0.5 list-disc list-inside text-amber-900">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="import-cancel-btn"
          >
            Close
          </Button>
          <Button
            onClick={runImport}
            disabled={busy || !csv.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="import-run-btn"
          >
            {busy ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Importing…</>
                  : <><Upload size={13} className="mr-1.5" /> Import to CRM</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
